// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: the dock's Calendar entry, after DMs and Favorites (calendar-plan.md sections 2 and 10), shown only to a
// calendar member. It copies FavoritesButton without the context menu. Exclude from upstream sync.

import {Routes} from '@app/app/Routes';
import styles from '@app/features/app/components/layout/GuildsLayout.module.css';
import {resolveGuildListIndicatorBarTarget} from '@app/features/app/components/layout/sidebar_nav/GuildListIndicator';
import {useHover} from '@app/features/app/hooks/useHover';
import {useMergeRefs} from '@app/features/app/hooks/useMergeRefs';
import {CalendarStrings as S} from '@app/features/calendar/CalendarStrings';
import Calendar from '@app/features/calendar/state/Calendar';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {useLocation} from '@app/features/platform/components/router/RouterReact';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {CalendarDotsIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import {useRef} from 'react';

interface CalendarButtonProps {
	className?: string;
}

export const CalendarButton = observer(({className}: CalendarButtonProps = {}) => {
	const [hoverRef, isHovering] = useHover();
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const iconRef = useRef<HTMLDivElement | null>(null);
	const mergedButtonRef = useMergeRefs([hoverRef, buttonRef]);
	const location = useLocation();
	const isSelected = location.pathname.startsWith(Routes.CALENDAR);
	const indicatorTarget = resolveGuildListIndicatorBarTarget({isSelected, showHoverState: isHovering});
	const isActive = isHovering || isSelected;
	if (!Calendar.isMember) {
		return null;
	}
	return (
		<Tooltip position="right" size="large" text={S.calendar} data-flx="app.sidebar-nav.calendar-button.tooltip">
			<FocusRing
				offset={-2}
				focusTarget={buttonRef}
				ringTarget={iconRef}
				data-flx="app.sidebar-nav.calendar-button.focus-ring"
			>
				<button
					type="button"
					className={clsx(styles.fluxerButton, className)}
					aria-label={S.calendar}
					aria-current={isSelected ? 'page' : undefined}
					data-guild-list-focus-item="true"
					onClick={() => RouterUtils.transitionTo(Routes.CALENDAR)}
					ref={mergedButtonRef}
					data-flx="app.sidebar-nav.calendar-button.fluxer-button.select"
				>
					<AnimatePresence data-flx="app.sidebar-nav.calendar-button.animate-presence">
						{(isSelected || isHovering) && (
							<div className={styles.guildIndicator} data-flx="app.sidebar-nav.calendar-button.guild-indicator">
								<motion.span
									className={styles.guildIndicatorBar}
									initial={false}
									animate={indicatorTarget}
									exit={{opacity: 0}}
									transition={{duration: 0.2, ease: [0.25, 0.1, 0.25, 1]}}
									data-flx="app.sidebar-nav.calendar-button.guild-indicator-bar"
								/>
							</div>
						)}
					</AnimatePresence>
					<div className={styles.relative} data-flx="app.sidebar-nav.calendar-button.relative">
						<motion.div
							ref={iconRef}
							className={clsx(styles.fluxerButtonIcon, isSelected && styles.fluxerButtonIconSelected)}
							animate={{borderRadius: isActive ? '30%' : '50%'}}
							initial={false}
							transition={{duration: 0.07, ease: 'easeOut'}}
							data-flx="app.sidebar-nav.calendar-button.fluxer-button-icon"
						>
							<CalendarDotsIcon
								weight="fill"
								className={styles.favoritesIcon}
								data-flx="app.sidebar-nav.calendar-button.calendar-icon"
							/>
						</motion.div>
					</div>
				</button>
			</FocusRing>
		</Tooltip>
	);
});
